import React, { useRef, useEffect } from 'react';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({ value, onChange, placeholder }) => {
  const editorRef = useRef<HTMLDivElement>(null);

  // Synchronize internal div content with incoming value, only when out of sync
  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value;
    }
  }, [value]);

  const execCommand = (command: string, arg: string = '') => {
    document.execCommand(command, false, arg);
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const handleInput = () => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  return (
    <div className="border border-solid border-black bg-white rounded-none p-2 mt-2 font-heebo">
      {/* Toolbar */}
      <div className="flex flex-wrap gap-1 pb-2 mb-2 border-b border-solid border-gray-300 select-none">
        <button
          type="button"
          onClick={() => execCommand('bold')}
          className="px-2 py-0.5 border border-solid border-gray-300 font-bold hover:bg-gray-100 text-xs rounded-none bg-white text-black"
          title="Bold (מודגש)"
        >
          B
        </button>
        <button
          type="button"
          onClick={() => execCommand('italic')}
          className="px-2 py-0.5 border border-solid border-gray-300 italic hover:bg-gray-100 text-xs rounded-none bg-white text-black"
          title="Italic (נטוי)"
        >
          I
        </button>
        <button
          type="button"
          onClick={() => execCommand('underline')}
          className="px-2 py-0.5 border border-solid border-gray-300 underline hover:bg-gray-100 text-xs rounded-none bg-white text-black"
          title="Underline (קו תחתון)"
        >
          U
        </button>
        <button
          type="button"
          onClick={() => execCommand('strikeThrough')}
          className="px-2 py-0.5 border border-solid border-gray-300 line-through hover:bg-gray-100 text-xs rounded-none bg-white text-black"
          title="Strike (קו חוצה)"
        >
          S
        </button>
        <button
          type="button"
          onClick={() => execCommand('insertUnorderedList')}
          className="px-2 py-0.5 border border-solid border-gray-300 hover:bg-gray-100 text-xs rounded-none bg-white text-black"
          title="Bullet List (רשימת תבליטים)"
        >
          • List
        </button>
        <button
          type="button"
          onClick={() => execCommand('insertOrderedList')}
          className="px-2 py-0.5 border border-solid border-gray-300 hover:bg-gray-100 text-xs rounded-none bg-white text-black"
          title="Numbered List (רשימה ממוספרת)"
        >
          1. List
        </button>
        <button
          type="button"
          onClick={() => {
            const url = prompt('Enter link URL:');
            if (url) execCommand('createLink', url);
          }}
          className="px-2 py-0.5 border border-solid border-gray-300 hover:bg-gray-100 text-xs rounded-none bg-white text-black"
          title="Add Link (הוסף קישור)"
        >
          🔗 Link
        </button>
        <button
          type="button"
          onClick={() => execCommand('removeFormat')}
          className="px-2 py-0.5 border border-solid border-gray-300 hover:bg-gray-100 text-[10px] rounded-none bg-white text-black"
          title="Clear Format (נקה עיצוב)"
        >
          Clear
        </button>
      </div>

      {/* Editable Container */}
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        className="outline-none min-h-[100px] max-h-[250px] overflow-auto text-xs text-black prose prose-sm max-w-none bg-white p-1 empty-placeholder"
        data-placeholder={placeholder}
        dir="rtl"
        style={{ direction: 'rtl', textAlign: 'right' }}
      />
    </div>
  );
};

export default RichTextEditor;